/**
 * @fileoverview StructuredData — Server Components that inject JSON-LD schema
 * for rich results in Google Search.
 *
 * Entity graph:
 *   Organization (@id: #organization)
 *     → publishes WebSite (@id: #website)
 *     → publishes SoftwareApplication (@id: #software)
 */

import type { FAQItemData } from '@/features/marketing/components/FAQ';
import { SITE_URL, siteUrl } from '@/lib/config';
import { businessInfo } from '@/lib/business-info';

function renderAnswer(answer: React.ReactNode): string {
  if (typeof answer === 'string') return answer;
  if (typeof answer === 'object' && answer !== null) {
    const extractText = (node: React.ReactNode): string => {
      if (typeof node === 'string') return node;
      if (typeof node === 'number') return String(node);
      if (Array.isArray(node)) return node.map(extractText).join('');
      if (typeof node === 'object' && node !== null && 'props' in (node as any)) {
        return extractText((node as any).props?.children ?? '');
      }
      return '';
    };
    return extractText(answer);
  }
  return '';
}

/** Stable @id URIs for the entity graph */
const ENTITY_IDS = {
  organization: `${SITE_URL}/#organization`,
  website: `${SITE_URL}/#website`,
  software: `${SITE_URL}/#software`,
} as const;

export function GlobalSchema() {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': ENTITY_IDS.organization,
    name: 'PostBrix',
    url: SITE_URL,
    logo: {
      '@type': 'ImageObject',
      url: siteUrl('/og-image.png'),
      width: 1200,
      height: 630,
    },
    sameAs: [
      businessInfo.socials.x,
      businessInfo.socials.linkedin,
      businessInfo.socials.instagram,
      businessInfo.socials.facebook,
      businessInfo.socials.youtube,
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: businessInfo.supportEmail,
      url: siteUrl('/contact'),
    },
  };

  const webSiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': ENTITY_IDS.website,
    name: 'PostBrix',
    url: SITE_URL,
    description:
      'Drag-and-drop MJML email builder for modern SaaS teams and agencies. Design, customize with AI, and export production-ready HTML.',
    publisher: {
      '@id': ENTITY_IDS.organization,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/documentation?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  const softwareApplicationSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': ENTITY_IDS.software,
    name: 'PostBrix Email Builder',
    operatingSystem: 'All',
    applicationCategory: 'DesignApplication',
    url: SITE_URL,
    description:
      'MJML-based drag-and-drop email builder. Design responsive email layouts visually, customize with AI, and export clean HTML or MJML to any ESP.',
    softwareVersion: '1.0',
    screenshot: siteUrl('/og-image.png'),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '1280',
      bestRating: '5',
      worstRating: '1',
    },
    featureList: [
      'Drag-and-drop email builder',
      'MJML and HTML export',
      'AI-assisted copy generation',
      'Responsive email templates',
      'Team collaboration',
      'Reusable email components',
      'Handlebars dynamic templating',
      'Email quality scoring engine',
    ],
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: '0',
      highPrice: '99',
      offerCount: '4',
      offers: [
        { '@type': 'Offer', name: 'Free', price: '0', priceCurrency: 'USD' },
        { '@type': 'Offer', name: 'Starter', price: '19', priceCurrency: 'USD' },
        { '@type': 'Offer', name: 'Pro', price: '49', priceCurrency: 'USD' },
        { '@type': 'Offer', name: 'Team', price: '99', priceCurrency: 'USD' },
      ],
    },
    publisher: {
      '@id': ENTITY_IDS.organization,
    },
  };

  const schemas = [organizationSchema, webSiteSchema, softwareApplicationSchema];

  return (
    <>
      {schemas.map((schema, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
    </>
  );
}

export function FAQSchema({ faqs }: { faqs: FAQItemData[] }) {
  if (!faqs || faqs.length === 0) return null;

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: renderAnswer(faq.answer),
      },
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
    />
  );
}

export function TemplateSchema({ template }: { template: any }) {
  if (!template) return null;
  
  let displayName = 'Untitled Template';
  if (typeof template.name === 'string') displayName = template.name;
  else if (template.name?.en || template.name?.name) displayName = template.name.en || template.name.name;

  const templateUrl = siteUrl(`/template/${template.slug || template.id}`);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: displayName,
    description: `Use the ${displayName} email template in PostBrix. Customize this ${template.category || 'beautiful'} template with our drag-and-drop editor.`,
    url: templateUrl,
    creator: {
      '@id': ENTITY_IDS.organization,
    },
    isAccessibleForFree: !template.isPremium,
    keywords: [template.category, 'email template', 'mjml template', 'responsive email'].filter(Boolean).join(', '),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function TemplateGallerySchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Free Email Templates Library | PostBrix',
    description: 'Browse our collection of beautiful, responsive, drag-and-drop email templates designed for PostBrix.',
    url: siteUrl('/template'),
    isPartOf: {
      '@id': ENTITY_IDS.website,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbSchema({ items }: { items: BreadcrumbItem[] }) {
  if (!items || items.length === 0) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : siteUrl(item.url),
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function PricingSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'PostBrix Email Builder',
    description: 'PostBrix visual drag-and-drop MJML email builder, AI writing assistant, and production-ready HTML export for modern SaaS teams.',
    image: siteUrl('/og-image.png'),
    brand: {
      '@id': ENTITY_IDS.organization,
    },
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'USD',
      lowPrice: '0',
      highPrice: '99',
      offerCount: '4',
      offers: [
        {
          '@type': 'Offer',
          name: 'Free Plan',
          price: '0',
          priceCurrency: 'USD',
          description: 'Perfect for trying the editor and side projects. 3 projects, 5 monthly exports.',
          url: siteUrl('/pricing'),
        },
        {
          '@type': 'Offer',
          name: 'Starter Plan',
          price: '19',
          priceCurrency: 'USD',
          description: 'For growing creators and freelance designers. 15 projects, unlimited exports.',
          url: siteUrl('/pricing'),
        },
        {
          '@type': 'Offer',
          name: 'Pro Plan',
          price: '49',
          priceCurrency: 'USD',
          description: 'For professional growth teams and agencies. Unlimited projects, full AI suite, and spam checks.',
          url: siteUrl('/pricing'),
        },
        {
          '@type': 'Offer',
          name: 'Team Plan',
          price: '99',
          priceCurrency: 'USD',
          description: 'For organizations with multi-seat collaboration, custom brand kits, and priority support.',
          url: siteUrl('/pricing'),
        },
      ],
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function DocsHubSchema({ totalArticles }: { totalArticles: number }) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'PostBrix Documentation & Architecture Center',
    description: `Complete technical guides, API references, MJML export documentation, and email client compatibility guides across ${totalArticles} articles.`,
    url: siteUrl('/documentation'),
    isPartOf: {
      '@id': ENTITY_IDS.website,
    },
    about: {
      '@id': ENTITY_IDS.software,
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/documentation?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export function WebPageSchema({
  title,
  description,
  url,
}: {
  title: string;
  description: string;
  url: string;
}) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: title,
    description: description,
    url: url.startsWith('http') ? url : siteUrl(url),
    isPartOf: {
      '@id': ENTITY_IDS.website,
    },
    publisher: {
      '@id': ENTITY_IDS.organization,
    },
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}
